import useSelectFirstRouteItem from "hooks/useSelectFirstRouteItem";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/store";
import { setTitlePreview } from "store/titleStore";
import { fetchWork } from "store/workStore";

import type { IWork, IWorkTitleSummary } from "./types";

const parsePositiveInt = (value: string | undefined): number | null => {
    if (value == null || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const previewFromWorkTitle = (titleSummary: IWorkTitleSummary) => ({
    id: titleSummary.id,
    name: titleSummary.name,
    ean13: titleSummary.ean13,
    avatarUrl: titleSummary.avatarUrl ?? undefined,
});

const useWorksSelection = () => {
    const { "*": worksSplat } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {
        works,
        worksStatus,
        isListRefreshing,
        selectedWork: fetchedWork,
        selectedWorkStatus,
    } = useAppSelector((state) => state.works);
    const currentTitleId = useAppSelector((state) => state.titles.title?.id);

    const splatSegments = (worksSplat ?? "").split("/").filter(Boolean);
    const selectedWorkId = parsePositiveInt(splatSegments[0]);
    const selectedTitleId = splatSegments[1] === "titles" ? parsePositiveInt(splatSegments[2]) : null;

    const [expandedWorkId, setExpandedWorkId] = useState<number | null>(selectedWorkId);
    const requestedWorkIdRef = useRef<number | null>(null);
    const worksById = useMemo(() => new Map(works.map((work) => [Number(work.id), work])), [works]);
    const isListReady = worksStatus !== "idle" && !isListRefreshing;
    const selectedWorkFromList = selectedWorkId == null ? null : worksById.get(selectedWorkId) ?? null;
    // List row wins. Otherwise the fetched fiche, as soon as its id is the route — list reload does not hide it.
    const selectedWork =
        selectedWorkFromList ?? (fetchedWork != null && Number(fetchedWork.id) === selectedWorkId ? fetchedWork : null);

    useSelectFirstRouteItem(works, selectedWorkId, "/works", setExpandedWorkId);

    useEffect(() => {
        if (selectedWorkId != null) setExpandedWorkId(selectedWorkId);
    }, [selectedWorkId]);

    useEffect(() => {
        if (selectedWorkId == null || !isListReady || worksById.has(selectedWorkId)) return;
        if (fetchedWork != null && Number(fetchedWork.id) === selectedWorkId) return;
        if (selectedWorkStatus === "pending") return;
        // The request for this id already failed (other company, deleted). Don't refetch in a loop.
        if (selectedWorkStatus === "failed" && requestedWorkIdRef.current === selectedWorkId) {
            navigate("/works", { replace: true });
            return;
        }
        requestedWorkIdRef.current = selectedWorkId;
        dispatch(fetchWork(selectedWorkId));
    }, [dispatch, fetchedWork, isListReady, navigate, selectedWorkId, selectedWorkStatus, worksById]);

    const previewTitleFromWork = useCallback(
        (workId: number, titleId: number) => {
            const work = worksById.get(workId);
            const titleSummary = work?.titles.find((workTitle) => workTitle.id === titleId);
            if (titleSummary) dispatch(setTitlePreview(previewFromWorkTitle(titleSummary)));
        },
        [dispatch, worksById],
    );

    useEffect(() => {
        if (selectedTitleId == null || selectedWorkId == null || currentTitleId === selectedTitleId) return;
        previewTitleFromWork(selectedWorkId, selectedTitleId);
    }, [currentTitleId, previewTitleFromWork, selectedTitleId, selectedWorkId]);

    const openWork = (work: IWork) => {
        const isSameWork = Number(work.id) === selectedWorkId;
        if (isSameWork && selectedTitleId != null) {
            setExpandedWorkId(work.id);
            navigate(`/works/${work.id}`);
            return;
        }
        setExpandedWorkId((currentId) => (currentId === work.id ? null : work.id));
        if (isSameWork) return;
        navigate(`/works/${work.id}`);
    };

    const toggleWorkExpand = (work: IWork) => {
        setExpandedWorkId((currentId) => (currentId === work.id ? null : work.id));
    };

    const openTitle = (workId: number, titleId: number) => {
        setExpandedWorkId(workId);
        if (titleId === selectedTitleId) return;
        previewTitleFromWork(workId, titleId);
        navigate(`/works/${workId}/titles/${titleId}`);
    };

    return {
        selectedWorkId,
        selectedTitleId,
        expandedWorkId,
        selectedWork,
        selectedWorkStatus,
        isListReady,
        openWork,
        toggleWorkExpand,
        openTitle,
    };
};

export default useWorksSelection;
